import React, { useState, useEffect, useRef } from 'react';
import { Dropdown, FormControl, Button } from 'react-bootstrap';
import { i18n } from '../../translate/i18n';

interface CreatableSelectProps {
  options: Array<{ id: string | number, name: string }>;
  placeholder?: string;
  placeholder2?: string;
  label: string;
  onSelect: (item: { id: string | number, name: string }) => void;
  onCreate: (name: string) => void;
  value?: { id: string | number, name: string } | null;
  disabled?: boolean;
}

export const CreatableSelect: React.FC<CreatableSelectProps> = ({
  options,
  placeholder,
  placeholder2,
  label,
  onSelect,
  onCreate,
  value = null,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchTerm) {
      const filtered = options.filter(option => 
        option.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option: { id: string | number, name: string }) => {
    onSelect(option);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleCreate = () => {
    if (searchTerm.trim()) {
      onCreate(searchTerm.trim());
      setSearchTerm('');
      setIsOpen(false);
    }
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const noMatchesFound = searchTerm && filteredOptions.length === 0;

  return (
    <div className="mt-4">
      <label className="form-label fw-normal">{label}</label>
      <div ref={dropdownRef} className="position-relative">
        <Dropdown show={isOpen} onToggle={toggleDropdown}>
          <Dropdown.Toggle 
            as="div" 
            className={`d-flex align-items-center form-control cursor-pointer fw-normal ${disabled ? 'disabled' : ''}`}
            style={{ cursor: 'pointer' }}
            disabled={disabled}
          >
            {value ? value.name : placeholder}
          </Dropdown.Toggle>

          <Dropdown.Menu className="w-100 p-2">
            <div className="px-2 mb-2">
              <FormControl
                autoFocus
                className="mx-0 w-100 fw-normal"
                placeholder={placeholder2}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {filteredOptions.map((option) => (
              <Dropdown.Item 
                key={option.id} 
                onClick={() => handleSelect(option)}
                active={value?.id === option.id}
              >
                {option.name}
              </Dropdown.Item>
            ))}
            
            {noMatchesFound && (
              <div className="px-2 py-1 border-top mt-2">
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  className="w-100 fw-normal"
                  onClick={handleCreate}
                >
                   {i18n.t(`buttons.create`)  } "{searchTerm}"
                </Button>
              </div>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
};